import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, CalendarCheck, CheckCircle2, Gauge, PartyPopper, PawPrint, X } from "lucide-react";

// Friendly 3-step onboarding checklist that guides new owners to their first Bond Score.
// Auto-detects progress from live data; dismissable; persisted per-user in localStorage.
export const OnboardingChecklist = ({ userId, role, petsCount, hasCheckin, hasScore }) => {
  const ackKey = `sp_onboarding_done_${userId || "anon"}`;
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try { setDismissed(!!localStorage.getItem(ackKey)); } catch (_) { setDismissed(false); }
  }, [ackKey]);

  if (role !== "owner" || dismissed) return null;

  const steps = [
    { key: "pet", done: petsCount > 0, icon: PawPrint, title: "Add your pet", hint: "Name, species, and what comforts them.", to: "/pets", cta: "Add pet" },
    { key: "checkin", done: hasCheckin, icon: CalendarCheck, title: "Do a 30-second check-in", hint: "Your mood and theirs — both sides of the bond.", to: "/check-ins", cta: "Check in" },
    { key: "score", done: hasScore, icon: Gauge, title: "See your first Bond Score", hint: "Generated automatically right after your first check-in.", to: "/bond-score", cta: "Calculate" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  const dismiss = () => {
    try { localStorage.setItem(ackKey, new Date().toISOString()); } catch (_) { /* noop */ }
    setDismissed(true);
  };

  return (
    <Card className="rounded-2xl mb-6 border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--accent)/0.18)]" data-testid="onboarding-checklist">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-sm flex items-center gap-2">
              {allDone ? <PartyPopper className="w-4 h-4 text-primary" /> : <Gauge className="w-4 h-4 text-primary" />}
              {allDone ? "You're all set — the bond work begins." : "Get to your first Bond Score"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="onboarding-progress-label">
              {allDone ? "All 3 steps complete. Keep checking in daily to sharpen your insights." : `${doneCount} of ${steps.length} steps done — about 2 minutes total.`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {allDone && (
              <Button size="sm" variant="secondary" className="rounded-lg h-8" onClick={dismiss} data-testid="onboarding-dismiss-button">Hide this</Button>
            )}
            <button onClick={dismiss} aria-label="Dismiss checklist" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="onboarding-close-button">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${(doneCount / steps.length) * 100}%` }} data-testid="onboarding-progress-bar" />
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {steps.map((s, i) => (
            <div key={s.key} className={`rounded-xl border p-3.5 flex flex-col ${s.done ? "border-[hsl(var(--success)/0.35)] bg-[hsl(var(--success)/0.06)]" : "border-border bg-card"}`} data-testid={`onboarding-step-${s.key}`}>
              <div className="flex items-center gap-2">
                {s.done
                  ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" data-testid={`onboarding-step-${s.key}-done`} />
                  : <span className="w-4 h-4 rounded-full border border-muted-foreground/40 text-[9px] font-semibold flex items-center justify-center text-muted-foreground">{i + 1}</span>}
                <p className={`text-sm font-medium ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.title}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex-1">{s.hint}</p>
              {!s.done && (
                <Link to={s.to} className="mt-2.5">
                  <Button size="sm" variant="outline" className="rounded-lg h-8 w-full" data-testid={`onboarding-step-${s.key}-cta`}>
                    {s.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
